<div class="flex w-5/12 min-w-fit grow gap-2">
    @if (index() !== 0) {
        <mat-form-field class="max-w-[92px] shrink" subscriptSizing="dynamic">
            <mat-select [(ngModel)]="filter.operand">
                <mat-option [value]="OperandTypeEnum.And">
                    {{ 'Components.SearchFilter.FilterOperands.And' | transloco }}
                </mat-option>
                <mat-option [value]="OperandTypeEnum.Or">
                    {{ 'Components.SearchFilter.FilterOperands.Or' | transloco }}
                </mat-option>
            </mat-select>
        </mat-form-field>
    }

    <mat-form-field class="grow" subscriptSizing="dynamic">
        <mat-select [(ngModel)]="filter.column" (selectionChange)="handleColumnChanged()">
            @for (prop of filterProperties(); track prop.columnName) {
                <mat-option [value]="prop.columnName">{{ prop.label }}</mat-option>
            }
        </mat-select>
    </mat-form-field>
</div>

<div class="flex w-6/12 grow-[9999] gap-2">
    <mat-form-field class="w-1/3 shrink-0 grow" subscriptSizing="dynamic">
        <mat-select
            [(ngModel)]="filter.comparisonOperator"
            [disabled]="!filter.column() || currentFilterComparisonOperators().length === 1">
            @for (operator of currentFilterComparisonOperators(); track operator.operator) {
                <mat-option [value]="operator.operator">
                    {{ operator.labelKey | transloco }}
                </mat-option>
            }
        </mat-select>
    </mat-form-field>

    @if (!!currentComparisonOperator()?.expectedParameterAmount) {
        @if (currentFilterProperty()?.allowedValues; as allowedValues) {
            <mat-form-field class="w-1/3 shrink-0 grow" subscriptSizing="dynamic">
                <mat-select [(ngModel)]="filter.value1" [disabled]="!filter.column()" [compareWith]="allowedValuesCompareFn">
                    @for (keyValue of allowedValues; track keyValue.key) {
                        <mat-option [value]="keyValue.key">
                            {{ keyValue.value }}
                        </mat-option>
                    }
                </mat-select>
            </mat-form-field>
        } @else {
            @switch (currentFilterProperty()?.type) {
                @case (FilterItemTypeEnum.Date) {
                    @if (filter.comparisonOperator() !== ComparisonOperatorsEnum.InRange) {
                        <mat-form-field subscriptSizing="dynamic" class="grow-[9999]">
                            <ng-container *ngTemplateOutlet="comparedWithPropertyTag" />
                            <input
                                matInput
                                [(ngModel)]="filter.value1"
                                [matDatepicker]="picker"
                                [placeholder]="dateFormatPlaceholder()"
                                [class.cdk-visually-hidden]="!!value1ComparedPropertyLabel()" />

                            <div matSuffix class="flex items-center gap-2">
                                <mat-datepicker-toggle [for]="picker" />
                                @if (canCompareWithAnotherProperty()) {
                                    <ng-container *ngTemplateOutlet="compareWithGroupBtn" />
                                }
                            </div>

                            <mat-datepicker #picker />
                        </mat-form-field>
                    } @else {
                        <mat-form-field subscriptSizing="dynamic" class="grow-[9999]">
                            <mat-date-range-input [rangePicker]="picker">
                                <input matStartDate [(ngModel)]="filter.value1" [placeholder]="dateFormatPlaceholder()" />
                                <input matEndDate [(ngModel)]="filter.value2" [placeholder]="dateFormatPlaceholder()" />
                            </mat-date-range-input>
                            <mat-datepicker-toggle matIconSuffix [for]="picker" />
                            <mat-date-range-picker #picker />
                        </mat-form-field>
                    }
                }
                @case (FilterItemTypeEnum.Time) {
                    <mat-form-field subscriptSizing="dynamic" class="grow-[9999]">
                        <mat-select [(ngModel)]="filter.value1" [disabled]="!filter.column()">
                            @for (time of allowedTimeValues; track time.value) {
                                <mat-option [value]="time.value">
                                    {{ time.label }}
                                </mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                    @if (filter.comparisonOperator() === ComparisonOperatorsEnum.InRange) {
                        <mat-form-field subscriptSizing="dynamic" class="grow-[9999]">
                            <mat-select [(ngModel)]="filter.value2" [disabled]="!filter.column()">
                                @for (time of allowedTimeValues; track time.value) {
                                    <mat-option [value]="time.value">
                                        {{ time.label }}
                                    </mat-option>
                                }
                            </mat-select>
                        </mat-form-field>
                    }
                }
                @case (FilterItemTypeEnum.Boolean) {
                    <!-- nothing -->
                }
                @case (FilterItemTypeEnum.Number) {
                    <mat-form-field subscriptSizing="dynamic" class="grow-[9999]">
                        <ng-container *ngTemplateOutlet="comparedWithPropertyTag" />
                        <input
                            matInput
                            arcNumberInput
                            [decimalPlaces]="4"
                            [class.cdk-visually-hidden]="!!value1ComparedPropertyLabel()"
                            [(ngModel)]="filter.value1" />
                        @if (canCompareWithAnotherProperty() && filter.comparisonOperator() !== ComparisonOperatorsEnum.InRange) {
                            <div matSuffix>
                                <ng-container *ngTemplateOutlet="compareWithGroupBtn" />
                            </div>
                        }
                    </mat-form-field>
                    @if (filter.comparisonOperator() === ComparisonOperatorsEnum.InRange) {
                        <mat-form-field subscriptSizing="dynamic" class="grow-[9999]">
                            <input matInput arcNumberInput [decimalPlaces]="4" [(ngModel)]="filter.value2" />
                        </mat-form-field>
                    }
                }
                @default {
                    <mat-form-field subscriptSizing="dynamic" class="grow-[9999]">
                        <ng-container *ngTemplateOutlet="comparedWithPropertyTag" />
                        <input matInput [(ngModel)]="filter.value1" [class.cdk-visually-hidden]="!!value1ComparedPropertyLabel()" />
                        @if (canCompareWithAnotherProperty()) {
                            <div matSuffix>
                                <ng-container *ngTemplateOutlet="compareWithGroupBtn" />
                            </div>
                        }
                    </mat-form-field>
                }
            }
        }
    }

    <div class="flex items-center">
        <button mat-icon-button (click)="handleRemove()">
            <mat-icon>close</mat-icon>
        </button>
    </div>
</div>

<ng-template #compareWithGroupBtn>
    <button
        mat-icon-button
        [matMenuTriggerFor]="compareWithGroupMenu"
        [matTooltip]="'Components.SearchFilter.CompareWithOther' | transloco">
        <mat-icon>data_object</mat-icon>
    </button>

    <mat-menu #compareWithGroupMenu="matMenu">
        @for (filterProperty of filterPropertiesSameGroup(); track filterProperty.columnName) {
            <button mat-menu-item (click)="setFilterValueCompareWith(filterProperty)">
                {{ filterProperty.label }}
            </button>
        }
    </mat-menu>
</ng-template>

<ng-template #comparedWithPropertyTag>
    @if (value1ComparedPropertyLabel()) {
        <div class="flex w-fit items-center justify-between gap-2 rounded-lg bg-primary px-2 py-0.5 text-sm text-on-primary">
            <p>{{ value1ComparedPropertyLabel() }}</p>
            <button (click)="setFilterValueCompareWith(undefined)">
                <arc-icon icon="close" [size]="12" />
            </button>
        </div>
    }
</ng-template>
